import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Loader2, ArrowLeft, Save, ShieldCheck, Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input } from "@/components/FormBits";
import { Avatar } from "@/components/Avatar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function MyAccount() {
  const navigate = useNavigate();
  const { user, changePassword, logout, refreshMe } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setPhotoBusy(true);
    try {
      await api.post("/auth/me/avatar", fd);
      await refreshMe();
      toast.success("Profile photo updated");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Upload failed");
    } finally {
      setPhotoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      await api.delete("/auth/me/avatar");
      await refreshMe();
      toast.success("Photo removed");
    } catch { toast.error("Could not remove photo"); }
    finally { setPhotoBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) return setErr("Password must be at least 8 characters.");
    if (next !== confirm) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="my-account-page">
        <Link to="/" className="rcw-btn-ghost text-sm mb-4">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="rcw-card p-6 sm:p-8">
          <div className="rcw-label">Account</div>
          <div className="mt-3 flex items-center gap-5">
            <Avatar name={user?.name} email={user?.email} src={user?.avatar_url} size="xl" data-testid="account-avatar" />
            <div>
              <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900">
                {user?.name || user?.email}
              </h1>
              <div className="mt-1 text-sm text-gray-600 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-amber-500" />
                {user?.email} · {user?.role === "admin" ? "Administrator" : "Standard user"}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <label className="rcw-btn-ghost text-xs cursor-pointer">
                  {photoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                  {user?.avatar_url ? "Change photo" : "Add photo"}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="account-photo-input" />
                </label>
                {user?.avatar_url && (
                  <button onClick={removePhoto} disabled={photoBusy} className="rcw-btn-ghost text-xs text-red-600" data-testid="account-photo-remove">
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="rcw-label">Change password</div>
            <form onSubmit={submit} className="mt-3 space-y-4 max-w-md">
              <Field label="Current password" required>
                <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                  data-testid="account-current" autoComplete="current-password" required />
              </Field>
              <Field label="New password" required>
                <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                  data-testid="account-new" autoComplete="new-password" required />
              </Field>
              <Field label="Confirm new password" required>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="account-confirm" autoComplete="new-password" required />
              </Field>
              {err ? <div className="text-sm text-red-600" data-testid="account-error">{err}</div> : null}
              <button type="submit" className="rcw-btn-primary" disabled={busy} data-testid="account-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save new password
              </button>
            </form>
          </div>

          <div className="mt-10 border-t border-gray-100 pt-6">
            <button
              onClick={() => { logout(); navigate("/login", { replace: true }); }}
              className="rcw-btn-ghost text-sm text-red-600 hover:bg-red-50"
              data-testid="account-logout"
            >
              <Lock className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
